// ✅ Importaciones necesarias de React, librerías y componentes personalizados
import { useState, useEffect } from 'react';
import { ButtonSiguiente, Input } from '../../ui';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';

// 🟢 URL base del backend
import { safeAxios } from '@/api/safeAxios';

const IntroduceNuevaContrasena: React.FC = () => {

    // Estados principales del formulario
    const [email, setEmail] = useState<string | null>(null);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [mensaje, setMensaje] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [successEffect, setSuccessEffect] = useState(false);

    const navigate = useNavigate();

    // ✅ PRELAUNCH
    const preLaunchActivo =
        String(import.meta.env.VITE_PRELAUNCH).toLowerCase() === "true";

    const loginRedirect = preLaunchActivo ? "/prelaunch" : "/sign";

    // 🔐 Datos recuperación
    const token = localStorage.getItem('token');
    const codigo = localStorage.getItem('resetCode');

    // 👁 Mostrar / ocultar contraseña
    const [showPassword, setShowPassword] = useState(false);

    // 🔹 Cargar email desde localStorage
    useEffect(() => {
        const storedEmail = localStorage.getItem('resetEmail');

        if (storedEmail) {
            setEmail(storedEmail);
        } else {
            setError('No se encontró un correo válido. Vuelve a solicitar el código.');
        }
    }, []);

    // 🔸 Validación formulario
    const validarFormulario = () => {

        if (!password || !confirmPassword) {
            return 'Los campos de contraseña no pueden estar vacíos.';
        }

        if (password !== confirmPassword) {
            return 'Las contraseñas no coinciden.';
        }

        if (password.length < 6) {
            return 'La contraseña debe tener al menos 6 caracteres.';
        }

        return '';
    };

    // 🔹 Submit
    const handleSubmit = async (e: React.FormEvent) => {

        e.preventDefault();

        setMensaje('');
        setError('');

        const errorValidacion = validarFormulario();

        if (errorValidacion) {
            setError(errorValidacion);
            return;
        }

        if (!email || !codigo) {
            setError('Faltan datos para restablecer la contraseña.');
            return;
        }

        setLoading(true);

        try {

            const response = await safeAxios(
                "/api/actualizarContrasena",
                navigate,
                {
                    method: "POST",
                    data: {
                        email,
                        codigo,
                        nuevaContrasena: password,
                        token
                    }
                }
            );

            // ✅ Correcto
            if (response.status === 200) {

                setSuccessEffect(true);

                setTimeout(() => {

                    setSuccessEffect(false);

                    // ✅ REDIRECCIÓN DINÁMICA
                    navigate(loginRedirect);

                }, 1000);

            } else {

                setError('Hubo un error al cambiar la contraseña.');

            }

        } catch (error: any) {

            setError(
                error.response?.data?.mensaje ||
                'Error al enviar la solicitud.'
            );

        } finally {

            setLoading(false);

        }
    };

    return (

        <form
            className="flex flex-col justify-center items-center h-screen w-full px-6"
            onSubmit={handleSubmit}
        >

            {/* CONTENEDOR */}
            <div
                className={`
                    w-full 
                    sm:w-11/12 
                    md:w-8/12 
                    lg:w-4/12 
                    xl:w-4/12 
                    2xl:w-3/12 
                    p-10 
                    space-y-4 
                    border-2 
                    rounded-lg 
                    border-zinc-600 
                    shadow-md 
                    transition-all 
                    duration-500
                    ${successEffect
                        ? 'shadow-green-500'
                        : 'shadow-zinc-600'
                    }
                `}
            >

                {/* EMAIL */}
                <div className="relative border-b border-white focus-within:border-white">

                    <Input
                        type="email"
                        id="email"
                        value={email || ''}
                        disabled
                        className="
                            w-full
                            text-white
                            bg-green-700
                            outline-none
                            py-3
                            min-h-[48px]
                            opacity-50
                            cursor-not-allowed
                        "
                    />

                </div>

                {/* PASSWORD */}
                <div className="relative border-b border-white focus-within:border-white">

                    <Input
                        type={showPassword ? "text" : "password"}
                        id="password"
                        placeholder="Contraseña"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="
                            w-full
                            outline-none
                            py-3
                            min-h-[48px]
                            pr-12
                            focus:border-green-600
                        "
                    />

                </div>

                {/* CONFIRM PASSWORD */}
                <div className="relative border-b border-white focus-within:border-white">

                    <Input
                        type={showPassword ? "text" : "password"}
                        id="confirm-password"
                        placeholder="Confirmar contraseña"
                        required
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="
                            w-full
                            outline-none
                            py-3
                            min-h-[48px]
                            pr-12
                            focus:border-green-600
                        "
                    />

                    {/* OJO */}
                    <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="
                            absolute
                            right-2
                            top-1/2
                            -translate-y-1/2
                            p-2
                        "
                    >
                        {showPassword
                            ? <Eye size={20} />
                            : <EyeOff size={20} />
                        }
                    </button>

                </div>

                {/* MENSAJES */}
                {mensaje && !error && (
                    <p className="text-green-500 text-sm">
                        {mensaje}
                    </p>
                )}

                {error && (
                    <p className="text-red-500 text-sm">
                        {error}
                    </p>
                )}

                {/* BOTÓN ENVIAR */}
                <div className="w-full">

                    <ButtonSiguiente
                        type="submit"
                        className="
                            w-full
                            bg-black
                            text-white
                            hover:text-black
                            font-bold
                            hover:bg-white
                            shadow-md
                            hover:shadow-blue-600
                        "
                        disabled={loading || !!validarFormulario()}
                    >
                        {loading ? 'Enviando...' : 'Enviar'}
                    </ButtonSiguiente>

                </div>

                {/* VOLVER */}
                   <div className="h-10 w-full text-center flex items-center justify-center transition-colors bg-zinc-600 shadow-md shadow-zinc-700 rounded-md duration-400 border active:shadow-blue-600 hover:border-blue-700 active:bg-white active:text-black text-white active:font-extrabold">

                    <Link
                        to={loginRedirect}
                        className="block"
                    >
                        Volver al inicio de sesión
                    </Link>

                </div>

            </div>

        </form>
    );
};

export default IntroduceNuevaContrasena;